import { Instagram, Facebook } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "../lib/scroll";
import { LEGAL_LINKS } from "../data/legal";
import {
    ADDRESS,
    DEV_TEL,
    FACEBOOK,
    HOURS,
    INSTAGRAM,
    NAV_LEFT,
    NAV_RIGHT,
    PHONE_DISPLAY,
    PHONE_TEL,
    SAL_URL,
    SOL_URL,
    WHATSAPP,
} from "../data/content";

const socialClass =
    "flex h-11 w-11 items-center justify-center rounded-full border transition-colors";

function socialHandlers(e, enter) {
    e.currentTarget.style.backgroundColor = enter ? "var(--ink)" : "transparent";
    e.currentTarget.style.color = enter ? "var(--bg)" : "inherit";
    e.currentTarget.style.borderColor = enter ? "var(--ink)" : "var(--line)";
}

export default function Footer() {
    const year = new Date().getFullYear();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const goToSection = (href) => {
        if (pathname === "/") scrollToId(href);
        else navigate("/" + href);
    };

    return (
        <footer
            data-testid="site-footer"
            className="px-6 py-20 md:px-12 md:py-24"
            style={{ backgroundColor: "var(--bg)" }}
        >
            <div className="mx-auto max-w-6xl">
                {/* Top */}
                <div className="grid grid-cols-1 gap-14 md:grid-cols-3 md:gap-10">
                    {/* Brand + socials (compact) */}
                    <div>
                        <span className="logo-frame inline-block">
                            <span className="font-serif text-2xl">
                                Lady
                                <span style={{ color: "var(--accent)" }}>Art</span>
                            </span>
                        </span>
                        <p
                            className="mt-4 max-w-xs font-sans text-sm leading-relaxed"
                            style={{ color: "var(--muted-foreground)" }}
                        >
                            Salon de coafură și culoare în Fălticeni.
                        </p>
                        <div className="mt-6 flex items-center gap-4">
                            <a
                                href={INSTAGRAM}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                data-testid="footer-instagram"
                                className={socialClass}
                                style={{ borderColor: "var(--line)" }}
                                onMouseEnter={(e) => socialHandlers(e, true)}
                                onMouseLeave={(e) => socialHandlers(e, false)}
                            >
                                <Instagram size={18} strokeWidth={1.4} />
                            </a>
                            <a
                                href={FACEBOOK}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                data-testid="footer-facebook"
                                className={socialClass}
                                style={{ borderColor: "var(--line)" }}
                                onMouseEnter={(e) => socialHandlers(e, true)}
                                onMouseLeave={(e) => socialHandlers(e, false)}
                            >
                                <Facebook size={18} strokeWidth={1.4} />
                            </a>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex flex-col gap-4 md:justify-self-center">
                        {[...NAV_LEFT, ...NAV_RIGHT].map((l) => (
                            <button
                                key={l.href}
                                type="button"
                                onClick={() => goToSection(l.href)}
                                className="eyebrow eyebrow-ink link-underline w-fit cursor-pointer border-0 bg-transparent p-0 text-left"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    {/* Salon info */}
                    <div className="flex flex-col gap-5 md:text-right">
                        <div>
                            <span className="eyebrow eyebrow-ink">Telefon</span>
                            <a
                                href={PHONE_TEL}
                                data-testid="footer-phone"
                                className="mt-2 block w-fit font-serif text-xl link-underline md:ml-auto"
                            >
                                {PHONE_DISPLAY}
                            </a>
                        </div>
                        <div>
                            <span className="eyebrow eyebrow-ink">Adresă</span>
                            <p
                                className="mt-2 font-sans text-sm leading-relaxed"
                                style={{ color: "var(--muted-foreground)" }}
                            >
                                {ADDRESS}
                            </p>
                        </div>
                        <div>
                            <span className="eyebrow eyebrow-ink">Program</span>
                            <div className="mt-2 flex flex-col gap-1">
                                {HOURS.map((h) => (
                                    <span
                                        key={h.day}
                                        className="font-sans text-sm"
                                        style={{ color: "var(--muted-foreground)" }}
                                    >
                                        {h.day}: {h.time}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <a
                            href={WHATSAPP}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-book-btn"
                            className="pill mt-2 w-fit px-8 py-3 font-sans text-[0.8rem] md:ml-auto"
                            style={{
                                backgroundColor: "var(--ink)",
                                color: "var(--bg)",
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor = "var(--accent)")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor = "var(--ink)")
                            }
                        >
                            Programează-te
                        </a>
                    </div>
                </div>

                {/* Compliance row — legal links + official banners on one line */}
                <div
                    className="mt-20 flex flex-col items-start justify-between gap-6 border-t pt-8 md:flex-row md:items-center"
                    style={{ borderColor: "var(--line)" }}
                >
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                        {LEGAL_LINKS.map((p) => (
                            <Link
                                key={p.to}
                                to={p.to}
                                className="font-sans text-xs link-underline cursor-pointer"
                                style={{ color: "var(--muted-foreground)" }}
                                data-testid={`legal-link-${p.to}`}
                            >
                                {p.label}
                            </Link>
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href={SAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="ANPC — Soluționarea Alternativă a Litigiilor"
                            data-testid="footer-anpc"
                            className="transition-opacity hover:opacity-80"
                        >
                            <img
                                src="/photos/anpc.webp"
                                alt="ANPC — SAL"
                                className="h-8 w-auto"
                                loading="lazy"
                            />
                        </a>
                        <a
                            href={SOL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Soluționarea Online a Litigiilor"
                            data-testid="footer-sol"
                            className="transition-opacity hover:opacity-80"
                        >
                            <img
                                src="/photos/sal.webp"
                                alt="SOL"
                                className="h-8 w-auto"
                                loading="lazy"
                            />
                        </a>
                    </div>
                </div>

                {/* Copyright + developer credit — single line */}
                <div className="mt-8">
                    <span className="eyebrow eyebrow-ink">
                        © {year} LadyArt Fălticeni • Toate drepturile rezervate
                    </span>
                </div>
            </div>
        </footer>
    );
}
